<script setup lang="ts">
// Modal de AVISO das promoções: a folha que sobe quando a roletinha ou o
// Check-in Diário não têm o que mostrar. Antes cada modal abria inteiro (topbar,
// logo, tagline) com uma frase solta no meio; aqui a folha é só arte de
// cabeçalho + o motivo +, quando o motivo é elegibilidade, o que falta pra
// virar usuário ativo.
//
// Figma: file MvX0d5Neu0A19lRCv3fsV4 → "Roleta" (285:6919) e "Check-in"
// (287:7903). Os dois frames são o MESMO desenho — só a imagem do cabeçalho
// muda —, então isto é um componente com a arte por prop.
//
// Ele não conhece store nem status: recebe arte, frase e o interruptor da seção
// de requisitos, então sobe num harness sem Pinia.
const props = withDefaults(
  defineProps<{
    // Arte do cabeçalho. Sangra pra CIMA da folha e tem a janela do Figma
    // (277,3 × 218,3), então a imagem precisa vir recortada nessa proporção — o
    // fade da base é feito aqui, em CSS.
    art: string;
    // O motivo, em uma frase. Varia por estado (sem roleta no ar, fora da
    // promoção, ...) — quem decide é quem abre a folha.
    message: string;
    // Segunda linha, menor. Só onde a copy existente tem uma (o 'unavailable' do
    // check-in promete a trilha pro próximo período).
    hint?: string;
    // Seção "o que falta pra ser usuário ativo". SÓ no estado de elegibilidade
    // (`is_eligible: false`) — nos outros motivos ela responderia outra pergunta.
    requirements?: boolean;
    // Cor das partículas do fundo. Cada feature tem a sua (azul na roletinha,
    // dourado no check-in), a MESMA que ela usa na folha cheia — o aviso é a
    // folha dela em outro estado, não uma tela de outro lugar.
    accent?: string;
  }>(),
  { hint: '', requirements: false, accent: '#5AA9FF' },
);

defineEmits<{ (e: 'close'): void }>();

// Passos FIXOS, não estado do jogador: o core só entrega o booleano
// `is_eligible` (GET /players/me), sem o status de cada condição. O ✓ verde é
// marcador de lista — nunca "você já cumpriu isto".
const REQUIREMENTS = ['Conta verificada (KYC)', '1 aposta com saldo real'];
</script>

<template>
  <div class="nts">
    <!-- Cabeçalho = arte + X, os dois FORA do painel: a arte sangra 75px acima
         dele (e o painel é `overflow: clip`, pelas quinas arredondadas), e o X
         precisa ficar na frente da arte. Esta caixa também é a régua do X: a
         altura dela é a da arte, então o `top` em % dele escala com o desenho. -->
    <div class="nts__head">
      <img
        alt=""
        aria-hidden="true"
        class="nts__art"
        :src="props.art"
      />

      <button
        aria-label="Fechar"
        class="nts__close"
        type="button"
        @click="$emit('close')"
      >
        <!-- Inline e não asset: as duas features têm o próprio close.svg, e uma
             base component apontando pra pasta de uma delas amarraria as duas. -->
        <svg
          aria-hidden="true"
          fill="none"
          viewBox="0 0 30 30"
        >
          <rect
            fill="#fff"
            fill-opacity="0.05"
            height="30"
            rx="15"
            width="30"
          />
          <path
            d="M19.2784 12.1583L20 11.4399L18.5601 10L17.8417 10.7184L15 13.5601L12.1583 10.7184L11.4399 10L10 11.4399L10.7184 12.1583L13.5601 15L10.7216 17.8417L10 18.5601L11.4399 20L12.1583 19.2816L15 16.4399L17.8417 19.2816L18.5601 20L20 18.5601L19.2816 17.8417L16.4399 15L19.2816 12.1583H19.2784Z"
            fill="#fff"
          />
        </svg>
      </button>
    </div>

    <div class="nts__panel">
      <!-- Partículas no rés do chão do painel (o painel já clipa e é
           `position: relative`, que é tudo o que a camada precisa). O `travel`
           tem que passar da altura do painel, senão o ciclo reinicia à vista:
           295px é o painel alto (com os requisitos) no tamanho do Figma. -->
      <BaseParticlesField
        class="nts__particles"
        :color="props.accent"
        travel="340px"
      />

      <p class="nts__message">{{ props.message }}</p>

      <p
        v-if="props.hint"
        class="nts__hint"
      >
        {{ props.hint }}
      </p>

      <!-- O divisor do Figma é a borda de cima deste bloco. -->
      <div
        v-if="props.requirements"
        class="nts__reqs"
      >
        <p class="nts__lead">
          Para se tornar um usuário ativo conclua todos esses passos abaixo:
        </p>

        <ul class="nts__list">
          <li
            v-for="item in REQUIREMENTS"
            :key="item"
            class="nts__item"
          >
            <svg
              aria-hidden="true"
              class="nts__check"
              fill="none"
              viewBox="0 0 16 16"
            >
              <circle
                cx="8"
                cy="8"
                fill="#16C265"
                fill-opacity="0.22"
                r="8"
              />
              <path
                d="M4.8 8.16 7.04 10.4l4.16-4.48"
                stroke="#16C265"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.6"
              />
            </svg>
            {{ item }}
          </li>
        </ul>
      </div>

      <!-- Ações do motivo (ex.: "Tentar novamente" do estado de erro). -->
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
// Bottom-sheet colado no rodapé, como as outras folhas destas duas features: a
// largura vai até o teto de 393px (a do Figma) e o resto é respiro.
// Todas as medidas verticais do desenho estão em PORCENTAGEM da largura (em CSS,
// padding e margin verticais também resolvem pela largura), e é isso que faz a
// peça escalar inteira, sem breakpoint: `36.47%` é o mesmo 143px do Figma quando
// a folha tem 393px.
// CUIDADO: essas % têm que ficar em filhos da folha, nunca NA folha. O bloco
// contenedor dela é o overlay (a tela toda), então uma % aqui viraria fração da
// VIEWPORT — foi assim que a arte subiu pra fora do painel na primeira versão.
.nts {
  @apply relative z-10 select-none;

  width: min(393px, 100vw);
  margin: 0 auto;

  img {
    -webkit-user-drag: none;
  }
}

// Janela do Figma (277,3 × 218,3), centrada com o mesmo empurrão de 5,5px pra
// direita (1,407% da folha). A imagem já vem recortada nessa proporção; `cover` é
// o cinto de segurança pra uma arte que chegue com outra (as rodas de VIP são
// 734×562).
// Ela fica NO FLUXO (dentro do cabeçalho), e é o painel que sobe por baixo dela
// (ver `.nts__panel`): posicionada de forma absoluta, a sangria de cima teria que
// ser reservada por um padding na folha — a % proibida do comentário acima.
// O fade da base é a máscara: opaco até 62,95% da altura (137,4 de 218,3 no
// Figma) e transparente no fim — é assim que a arte "dissolve" no painel em vez
// de terminar numa aresta.
.nts__head {
  @apply relative z-[2];
}

.nts__art {
  position: relative;
  left: 1.407%;
  display: block;
  width: 70.56%;
  margin: 0 auto;
  aspect-ratio: 277.291 / 218.318;
  object-fit: cover;
  pointer-events: none;
  // Prefixado junto: `mask-image` sem prefixo só vale de iOS 15.4 pra cá, e sem
  // a máscara a arte terminaria numa aresta reta no meio do painel.
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 0,
    #000 62.95%,
    transparent 100%
  );
  mask-image: linear-gradient(to bottom, #000 0, #000 62.95%, transparent 100%);
}

// `margin-top` negativo = os 143px que a arte entra no painel (a sangria de 75px
// dela sobra acima); `padding-top` = os 151px do Figma até a frase, o que deixa
// ~8px de respiro entre a base da arte e o texto.
// z abaixo da arte pro fundo do painel não cobri-la.
.nts__panel {
  position: relative;
  z-index: 1;
  margin-top: -36.47%;
  padding: 38.42% 19px calc(env(safe-area-inset-bottom, 0px) + 33px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  // Colado na base: só os cantos de cima arredondam.
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: #000717;
  // `clip` e não `hidden`: `hidden` é scroll container, e aí o ajuste de foco do
  // clique desloca o painel (o mesmo bug das bordas da roletinha).
  overflow: clip;
}

// Os 11px abaixo do topo do painel do Figma, medidos DENTRO do cabeçalho: o
// painel começa a 75px do topo da arte, então o X cai em (75 + 11,17) / 218,318 da
// altura dela. Em % pra acompanhar a escala — e daí ele precisar do cabeçalho como
// régua, já que % em `top` resolve pela ALTURA do bloco contenedor.
.nts__close {
  @apply absolute z-[3];

  top: 39.47%;
  right: 14px;
  width: 30px;
  height: 30px;

  svg {
    @apply w-full h-full;
  }
}

// Partículas no chão do painel e o conteúdo um degrau acima: sem
// `position: relative` o texto não participa da ordem de pilha e uma camada em
// z-0 pinta por cima dele, por mais que ela venha antes no DOM.
.nts__particles {
  z-index: 0;
  // 30% mais discretas que no resto da casa: a folha de aviso é quase só texto
  // (não tem roda nem trilha competindo com o fundo), e no cheio as partículas
  // puxavam a leitura pra elas. A opacidade vai na CAMADA e não no componente:
  // o pico de cada ponto é sorteado lá dentro (0,35–0,9) e mexer nele mudaria o
  // fundo da roletinha e do check-in junto.
  opacity: 0.7;
}

.nts__message,
.nts__hint {
  @apply relative z-[1];
}

// Halo escuro atrás de TODO texto da folha: as partículas passam por baixo das
// letras (elas estão em z-0, o texto em z-1) e, quando um ponto brilhante cruza
// uma linha, o contraste local cai e a leitura tropeça. A sombra é da cor do
// próprio painel (#000717), então ela não se lê como sombra — só reforça o fundo
// no raio da letra: uma cópia curta e dura pra apoiar o traço, e uma larga e
// suave pra abafar o halo do ponto.
// Aqui não vale `text-shadow` numa regra só no `.nts__panel`: ela é herdada, e
// herdar uma sombra é o mesmo que declarar, então tanto faz — mas listar os
// alvos deixa explícito o que é texto de leitura e o que não é (o ✓ da lista é
// SVG e não recebe nada).
.nts__message,
.nts__hint,
.nts__lead,
.nts__item {
  text-shadow:
    0 1px 2px rgba(0, 7, 23, 0.9),
    0 0 10px rgba(0, 7, 23, 0.75);
}

.nts__message {
  @apply text-white font-bold;

  font-size: 18px;
  line-height: 26px;
}

.nts__hint {
  @apply text-white/60;

  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
}

.nts__reqs {
  @apply relative z-[1];

  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.nts__lead {
  @apply text-white font-normal;

  font-size: 14px;
  line-height: 22px;
}

.nts__list {
  @apply flex flex-col;

  gap: 8px;
  margin-top: 16px;
}

.nts__item {
  @apply flex items-center text-white/85 font-normal;

  gap: 10px;
  font-size: 12px;
  line-height: 17px;
}

.nts__check {
  @apply shrink-0;

  width: 16px;
  height: 16px;
}

// Desktop: card centrado, como as outras folhas viram card no mesmo ponto.
@media (min-width: 1024px) {
  .nts__panel {
    padding-bottom: 33px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
  }
}
</style>
